import { useLayoutEffect, useMemo, useRef } from 'react';
import { InstancedMesh, Matrix4, Vector3, Quaternion, Euler, ExtrudeGeometry } from 'three';
import { roundTubeProfile } from '../parametric/crossSections';
import { SCALE_IN_TO_UNIT } from '../types';
import { MATERIALS } from '../materials';
import type { HardwareGroup } from './instances';
import { useSceneSelection } from '../selection/useSceneSelection';

interface Props {
  group: HardwareGroup;
  heightIn: number;
}

export function BalusterField({ group, heightIn }: Props) {
  const ref = useRef<InstancedMesh>(null);
  const VISUAL_SCALE = 2.5; // cross-section visibility (real tube 0.75" OD, real height kept)
  const geometry = useMemo(() => {
    const g = new ExtrudeGeometry(roundTubeProfile(12), { depth: heightIn, bevelEnabled: false });
    g.scale(SCALE_IN_TO_UNIT * VISUAL_SCALE, SCALE_IN_TO_UNIT * VISUAL_SCALE, SCALE_IN_TO_UNIT);
    g.rotateX(-Math.PI / 2);
    return g;
  }, [heightIn]);

  const material = MATERIALS.aluminumBrushed;

  useLayoutEffect(() => {
    if (!ref.current) return;
    const m = new Matrix4();
    group.placements.forEach((p, i) => {
      m.compose(
        new Vector3(
          p.position.x * SCALE_IN_TO_UNIT,
          p.position.y * SCALE_IN_TO_UNIT,
          p.position.z * SCALE_IN_TO_UNIT,
        ),
        new Quaternion().setFromEuler(new Euler(p.rotation.x, p.rotation.y, p.rotation.z)),
        new Vector3(1, 1, 1),
      );
      ref.current!.setMatrixAt(i, m);
    });
    ref.current.instanceMatrix.needsUpdate = true;
  }, [group.placements]);

  if (group.placements.length === 0) return null;

  return (
    <instancedMesh
      ref={ref}
      args={[geometry, material, group.placements.length]}
      castShadow
      userData={{ partNumber: group.partNumber, kind: 'hardware-field', geometry: group.geometry }}
      onPointerOver={(e) => {
        e.stopPropagation();
        const id = e.instanceId;
        if (id == null) return;
        const ref = group.placements[id]?.parentRefId ?? group.partNumber;
        useSceneSelection.getState().setHovered(ref);
      }}
      onPointerOut={(e) => {
        e.stopPropagation();
        useSceneSelection.getState().setHovered(null);
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        const id = e.instanceId;
        if (id == null) return;
        const ref = group.placements[id]?.parentRefId ?? group.partNumber;
        useSceneSelection.getState().setSelected(ref);
      }}
    />
  );
}
